import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';

/**
 * Helper to fetch a file as an ArrayBuffer, typically from a Supabase Storage URL
 */
export const fetchTemplateBuffer = async (url: string): Promise<ArrayBuffer> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch template from ${url}. Status: ${response.status}`);
    }
    return await response.arrayBuffer();
};

/**
 * Parses the Word template in real-time to extract all shortcodes enclosed in square brackets.
 * Returns an array of unique custom field names, filtering out standard fields.
 */
export const extractTemplateFields = (templateBuffer: ArrayBuffer): string[] => {
    try {
        const zip = new PizZip(templateBuffer);
        const xmlFiles = zip.file(/\.xml$/);

        let allTags = new Set<string>();

        xmlFiles.forEach((file: any) => {
            let xml = file.asText();

            // 1. Remove Word tracking and spell-check nodes to prevent tag splitting like [ t a g ]
            xml = xml.replace(/<w:del[\s>][\s\S]*?<\/w:del>/gi, "");
            xml = xml.replace(/<w:ins[\s>][\s\S]*?>/gi, "");
            xml = xml.replace(/<\/w:ins>/gi, "");
            xml = xml.replace(/<w:proofErr[\s>][\s\S]*?>/gi, "");
            xml = xml.replace(/<w:lang[\s>][\s\S]*?>/gi, "");
            xml = xml.replace(/<w:noProof[\s>][\s\S]*?>/gi, "");

            // 2. Clear out all the standard XML tags like <w:t>  </w:t> so we're left with pure text chunks
            const rawText = xml.replace(/<[^>]+>/g, '');

            // 3. Match everything inside square brackets: [some_tag]
            // Standardizing based on docxtemplater delimiters being set to '[' and ']'
            const matches = [...rawText.matchAll(/\[(.*?)\]/g)];

            matches.forEach(match => {
                const tag = match[1].trim();
                // Filter out standard keys, structural loop tags, and indexed array tags (e.g. experience1_role)
                if (
                    ![
                        'name', 'email', 'phone', 'location', 'linkedin', 'role', 'summary', 'skills', 'skill',
                        '#workHistory', '/workHistory', 'workHistory',
                        '#education', '/education', 'education',
                        'company', 'period', 'description', 'school', 'degree', 'year',
                        // New aliases and loops
                        '#eis', '/eis', 'eis', 'eistitel', 'eistekst',
                        '#wens', '/wens', 'wens', 'wenstitel', 'wenstekst',
                        '#course', '/course', 'course', 'coursetitel', 'courseinstitute', 'courseyear',
                        '#skills', '/skills', '#skill', '/skill', 'skilldetail',
                        'educationschool', 'educationyear'
                    ].includes(tag) &&
                    !tag.startsWith('#') && // Ignore standard loop opening tags
                    !tag.startsWith('/') && // Ignore standard loop closing tags
                    !/^experience\d+_/.test(tag) && // Ignore experience1_role, experience2_company, etc.
                    !/^education\d+_/.test(tag) // Ignore education1_degree, etc.
                ) {
                    allTags.add(tag);
                }
            });
        });

        return Array.from(allTags);
    } catch (error) {
        console.error("Fout bij extractie van dynamische template fields:", error);
        return []; // Fail gracefully, returning no extra fields
    }
};

/**
 * Takes a Word Template (.docx) ArrayBuffer and an object of data,
 * and returns a new Blob containing the filled Word Document.
 * 
 * @param templateBuffer The raw bytes of the .docx template
 * @param data The object containing keys that map to {{tags}} in the document
 * @returns A Blob representing the filled .docx file
 */
export const generateWordDocument = (templateBuffer: ArrayBuffer, data: Record<string, any>): Blob => {
    // Load the docx file as a zip
    const zip = new PizZip(templateBuffer);

    try {
        // Initialize docxtemplater (Parsing happens here, throws XTTemplateError on bad tags)
        const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
            nullGetter() {
                return ""; // If a tag like {{phone}} is missing in data, just leave it blank
            },
        });

        // --- THE MAGIC FIX FOR WORD SPELL CHECKER TAGS --- 
        // Word often chops up tags like { { s u m m a r y } } into separate XML nodes because of spellcheck (<w:proofErr>).
        // Docxtemplater has a built-in resolveData or module system, but for simple node fragmentation,
        // we can use the official trick: removing proofErr and rsid tags before parsing, OR
        // resolving the zip entries manually.
        // However, docxtemplater v3.30+ handles some of this automatically if we use the correct error handlers.
        // If the error explicitly says "duplicate open tags", it means the lexer found `{` then XML then `{`.
        // The most robust community fix is running a regex over document.xml to strip <w:proofErr> tags.

        // PRE-PROCESS ALL XML FILES IN THE DOCX (Header, Footer, Document)
        // This is necessary because MS Word's Spellchecker (<w:proofErr>) and 
        // Track Changes (<w:ins>/<w:del>) often split our {{tags}} into fragmented XML nodes:
        // Example: <w:t>{</w:t><w:proofErr/><w:t>{ n a m e } } </w:t>
        // Docxtemplater crashes on these with "duplicate open tags" or "unclosed tag".

        const xmlFiles = zip.file(/\.xml$/);
        xmlFiles.forEach((file: any) => {
            let xml = file.asText();

            // 1. Completely remove Tracked Deletions (text the user deleted but Word still hides in the file)
            xml = xml.replace(/<w:del[\s>][\s\S]*?<\/w:del>/gi, "");

            // 2. Remove Tracked Insertions wrappers (keep the text inside)
            xml = xml.replace(/<w:ins[\s>][\s\S]*?>/gi, "");
            xml = xml.replace(/<\/w:ins>/gi, "");

            // 3. Remove spellcheck and grammar check tags that interrupt text
            xml = xml.replace(/<w:proofErr[\s>][\s\S]*?>/gi, "");
            xml = xml.replace(/<w:lang[\s>][\s\S]*?>/gi, "");
            xml = xml.replace(/<w:noProof[\s>][\s\S]*?>/gi, "");

            // 4. THE ULTIMATE BRACKET FIX: Square Brackets
            // If the user typed [[name]] or Word doubled it up due to XML: collapse it to [name]
            // We only need one bracket now: [tag]

            // Only fix brackets inside actual text nodes to avoid destroying the document structure
            // If we see 2+ brackets in a row, collapse them to 1.
            xml = xml.replace(/\[{2,}/g, "[");
            xml = xml.replace(/\]{2,}/g, "]");

            // 5. THE PURGE: Docxtemplater STILL crashes on `{{` even when delimiters are `[ ]`.
            // Because you switched to square brackets, we can now safely DESTROY all double curly braces 
            // from the XML. This ensures any old `{{workHistory}}` tags (or Word's ghost track changes) NEVER break the parser.
            xml = xml.replace(/\{\{/g, " ");
            xml = xml.replace(/\}\}/g, " ");

            zip.file(file.name, xml);
        });

        // Initialize docxtemplater
        // To suppress MultiErrors from throwing, docxtemplater recommends using a custom parser or ignoring lexer errors
        // Unfortunately standard free docxtemplater throws hard on Lexer errors.
        const cleanDoc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
            delimiters: {
                start: '[',
                end: ']'
            },
            nullGetter() { return ""; }
        });

        // Render the document
        cleanDoc.render(data);

        // Generate the resulting zip file as a Blob
        return cleanDoc.getZip().generate({
            type: 'blob',
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
    } catch (error: any) {
        // Advanced error handling from docxtemplater docs
        console.error("FULL DOCXTEMPLATER ERROR", error);

        // Handle parsing errors (e.g. unclosed tags) which are wrapped in properties.errors
        if (error.properties && error.properties.errors instanceof Array) {
            const errorMessages = error.properties.errors.map((e: any) => e.properties.explanation || e.message).join("\\n");
            console.error('Docxtemplater parsing errors', errorMessages);
            throw new Error('Kon het Word template niet lezen. Controleer de tags (bijv. ongesloten tags):\\n' + errorMessages);
        }

        throw new Error('Er zit een fout in het Word document template: ' + (error.properties?.explanation || error.message));
    }
};
